import { useState } from 'react'
import type { PricingPlan } from '../session/queries'
import { useI18n } from '../i18n/LocaleContext'
import { cn } from '../lib/cn'
import { defaultPlan, monthsFree, perMonthCents } from '../lib/pricing'
import { Button } from './Button'
import { CheckIcon } from './icons'

/**
 * The one offer, as a single card: what the product includes on the left,
 * the trial on the right with a monthly / yearly switch. Pricing and the
 * paywall both render it; only the button's variant and what happens on
 * select differ.
 */
export function PlanPicker({
  plans,
  trialDays,
  pendingPlanId,
  onSelect,
  variant = 'primary',
}: {
  plans: PricingPlan[]
  trialDays: number
  pendingPlanId: string | null
  onSelect: (planId: string) => void
  variant?: 'primary' | 'secondary'
}) {
  const { t, fmt, formatMoney } = useI18n()
  const [chosenId, setChosenId] = useState<string | null>(null)
  const plan = plans.find((p) => p.id === chosenId) ?? defaultPlan(plans)
  if (!plan) return null

  const currency = (plan.currency ?? 'eur').toUpperCase()
  const features = Array.isArray(plan.features) ? plan.features : []
  const price = formatMoney(plan.price_cents, currency)
  const then =
    plan.interval === 'year'
      ? fmt(t.pricing.thenYearly, { price, perMonth: formatMoney(perMonthCents(plan), currency) })
      : fmt(t.pricing.thenMonthly, { price })
  const pending = pendingPlanId === plan.id

  return (
    <div className="grid overflow-hidden rounded-2xl bg-surface p-2 shadow-card md:grid-cols-2">
      <div className="px-6 py-7 sm:px-8">
        <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500">{t.pricing.whatYouGet}</p>
        {features.length > 0 && (
          <ul className="mt-5 space-y-3.5">
            {features.map((feature) => (
              <li key={feature} className="flex items-start gap-3 text-[15px] leading-snug text-gray-800">
                <span className="mt-0.5 shrink-0 text-sage-700">
                  <CheckIcon size={16} />
                </span>
                {feature}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* The trial is the offer; the price is what follows it. */}
      <div className="rounded-xl bg-sage-100 px-6 py-7 sm:px-8">
        <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-sage-800">{t.pricing.trialKicker}</p>
        <p className="mt-1 font-display text-[40px] leading-tight tracking-tightish text-gray-900">
          {fmt(t.pricing.trialHeadline, { n: trialDays })}
        </p>

        {plans.length > 1 && (
          <div
            role="radiogroup"
            aria-label={t.pricing.intervalLabel}
            className="mt-6 grid grid-cols-2 rounded-full bg-sage-50/70 p-1 text-[14px] font-semibold"
          >
            {plans.map((p) => {
              const active = p.id === plan.id
              const free = monthsFree(p, plans)
              return (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setChosenId(p.id)}
                  className={cn(
                    'flex h-11 items-center justify-center gap-2 rounded-full transition-colors',
                    active ? 'bg-gray-900 text-gray-50' : 'text-gray-700 hover:text-gray-900',
                  )}
                >
                  {p.interval === 'year' ? t.pricing.yearly : t.pricing.monthly}
                  {free !== null && (
                    <span className="rounded-full bg-brand-100 px-2 py-0.5 text-[11.5px] font-bold text-brand-800">
                      {fmt(t.pricing.monthsFree, { n: free })}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}

        <p className="mt-4 text-[14px] text-gray-700">{then}</p>

        <Button
          size="lg"
          variant={variant}
          className="mt-6 w-full"
          onClick={() => onSelect(plan.id)}
          disabled={!!pendingPlanId}
        >
          {pending ? t.pricing.submitting : fmt(t.pricing.selectPlan, { n: trialDays })}
        </Button>
      </div>
    </div>
  )
}
